import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { QrCode, Lock } from "lucide-react";
import { freshnessColors, freshnessLabel, boxLabel } from "../lib/format";
import { boxColorStyle } from "./BoxColorSwatch";

/** Compact, square-ish grid card for the Inventory page. */
export const ContainerCardCompact = ({ container }) => {
  const c = container;
  const colors = freshnessColors(c);
  // Box-color stripe — only render when the user actually picked a color
  // (anything other than "clear" / unset). Leaves untagged cards flat.
  const hasBoxColor = c.box_color && c.box_color !== "clear";
  const boxColor = hasBoxColor ? boxColorStyle(c.box_color).backgroundColor : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -3 }}
      data-testid={`container-card-compact-${c.id}`}
      className="tup-card overflow-hidden"
    >
      {hasBoxColor && (
        <div
          className="h-1.5 w-full"
          style={{ backgroundColor: boxColor }}
          data-testid={`box-color-bar-compact-${c.id}`}
          aria-label={`Box colour: ${c.box_color}`}
        />
      )}
      <Link to={`/container/${c.id}`} className="block p-3">
        <div className="flex items-center justify-between gap-2">
          {boxLabel(c) ? (
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-[#DCEDF0] text-[#1F5566] border border-[#277A82]/30 shrink-0"
              data-testid={`box-label-compact-${c.id}`}
            >
              {boxLabel(c)}
            </span>
          ) : <span />}
          <div className="flex items-center gap-1.5">
            {c.is_personal && (
              <Lock
                className="w-3.5 h-3.5 text-[#277A82] shrink-0"
                strokeWidth={2.4}
                data-testid={`personal-lock-compact-${c.id}`}
                aria-label="Personal storing"
              />
            )}
            {c.logged_via_scan && (
              <QrCode
                className="w-5 h-5 text-[#4A8A78] shrink-0"
                strokeWidth={2.4}
                data-testid={`scanned-badge-compact-${c.id}`}
                aria-label="Logged via QR scan"
              />
            )}
          </div>
        </div>
        <div
          className="text-[10px] font-bold px-2 py-0.5 rounded-full truncate inline-block mt-2"
          style={{ backgroundColor: colors.bg, color: colors.fg }}
        >
          {freshnessLabel(c)}
        </div>

        <h3 className="font-heading text-base font-semibold text-[#2D3142] truncate mt-3">
          {c.name}
        </h3>
        {(c.dietary_tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {c.dietary_tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#DCEDF0] text-[#1F5566]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1 text-[10px] text-[#4F5D75]">
          <span className="font-semibold text-[#1F5566]">Loc:</span>
          <span className="truncate">{c.location_name || "Fridge"}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] text-[#8A93A6]">
          <span className="font-mono">{c.qr_code}</span>
          <span className="font-semibold" style={{ color: colors.fg }}>View →</span>
        </div>
      </Link>
    </motion.div>
  );
};
